import { Inject, inject, Injectable } from '@angular/core';
import { MyOrganizationService } from 'libs/data/src/lib/services/my-organization/my-organization.service';
import { CustomModalService } from '../modal/custom-modal.service';
import { catchError, finalize, of, Subject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { BsModalRef } from 'ngx-bootstrap/modal';
import {
  AccessPermissionService,
  ADD_USER,
  mainModuleName,
  rolesModuleName,
  UPDATE_USER,
} from '@piqet-angular/shared';

@Injectable({
  providedIn: 'root',
})
export class ManageUserService {
  myOrganizationService = inject(MyOrganizationService);
  modal = inject(CustomModalService);
  toaster = inject(ToastrService);
  accessService = inject(AccessPermissionService);
  activeRoute!: string;

  // Store the current modal reference
  private currentModalRef: BsModalRef | null = null;

  private getUserListMethod = new Subject<void>();
  getUserListMethodCalled$ = this.getUserListMethod.asObservable();

  private getUserInfoMethod = new Subject<void>();
  getUserInfoMethodCalled$ = this.getUserInfoMethod.asObservable();

  isAccess = (pathName: any, type: 'view' | 'edit' | 'create' | 'delete') => {
    return pathName.includes('admin')
      ? this.accessService.hasModulePermission(
          rolesModuleName.AdministratorUser,
          mainModuleName.userManagementModule,
          type
        )
      : this.accessService.hasModulePermission(
          rolesModuleName.myOrganizationManageUser,
          mainModuleName.manageOrganizationModule,
          type
        );
  };

  async editUser(
    myOrganizationManageUserId: any,
    actionLabel: string,
    pathName: any,
    organizationId: any,
    isEdit: boolean
  ) {
    this.activeRoute = pathName;

    if (pathName.includes('manageUsers') || pathName.includes('AdminManageUsers')) {
      if (actionLabel === ADD_USER || actionLabel === 'Add new user') {
        try {
          const res = await this.myOrganizationService.getManageUserDataForAdd(0);

          // Store the modal reference
          this.currentModalRef = this.modal.invoke('newMyOrganizationManageUser', {
            initialState: {
              newMyOrganizationManageUserData: res.data,
              label: actionLabel,
              isEdit,
              orgName: localStorage.getItem('oName'),
            },
            class: 'modal-xl',
          });

          // Subscribe using the stored reference
          this.subscribeToSaveUserEvent(this.currentModalRef);
        } catch (error) {
          console.error('Error opening add user modal:', error);
          this.toaster.error('Failed to open user form');
        }
      } else if (actionLabel === UPDATE_USER) {
        try {
          const res = await this.myOrganizationService.getManageUserDataForEdit(
            myOrganizationManageUserId,
            organizationId
          );

          // Store the modal reference
          this.currentModalRef = this.modal.invoke('newMyOrganizationManageUser', {
            initialState: {
              newMyOrganizationManageUserData: res.data,
              label: actionLabel,
              isEdit,
            },
            class: 'modal-xl',
          });

          // Subscribe using the stored reference
          this.subscribeToSaveUserEvent(this.currentModalRef);
        } catch (error) {
          console.error('Error opening edit user modal:', error);
          this.toaster.error('Failed to open user form');
        }
      }
    }
  }

  private subscribeToSaveUserEvent(modalRef: BsModalRef | null) {
    if (!modalRef || !modalRef.content) {
      console.error('Modal reference is not available');
      return;
    }

    // Check if the content has the saveManageUserEvent
    const modalContent = modalRef.content as any;

    if (modalContent.saveManageUserEvent) {
      // Subscribe to the save event
      const subscription = modalContent.saveManageUserEvent.subscribe(
        (saveManageUserEvent: any) => {
          this.saveUser(saveManageUserEvent.payload);
        }
      );

      // Clean up subscription when modal is hidden
      modalRef.onHidden?.subscribe(() => {
        subscription.unsubscribe();
        this.currentModalRef = null;
      });
    } else {
      console.error('saveManageUserEvent not found on modal content');

      // Try alternative approach - wait for the content to be ready
      setTimeout(() => {
        if (modalContent.saveManageUserEvent) {
          const subscription = modalContent.saveManageUserEvent.subscribe(
            (saveManageUserEvent: any) => {
              this.saveUser(saveManageUserEvent.payload);
            }
          );

          modalRef.onHidden?.subscribe(() => {
            subscription.unsubscribe();
            this.currentModalRef = null;
          });
        }
      }, 100);
    }
  }

  getUserList() {
    this.getUserListMethod.next();
  }

  saveUser(userPayload: any) {
    if (this.activeRoute.includes('manageUsers') || this.activeRoute.includes('AdminManageUsers')) {
      const userServiceCall =
        userPayload.userId === 0
          ? this.myOrganizationService.addManageUser(userPayload)
          : this.myOrganizationService.SaveMyOrganizationEditUserForEdit(userPayload);

      userServiceCall
        .pipe(
          catchError((error: any) => {
            console.error('Error saving user:', error);
            this.toaster.error('An error occurred. Please try again later.');
            return of(null);
          }),
          finalize(() => {
            // Close the current modal
            if (this.currentModalRef) {
              this.currentModalRef.hide();
              this.currentModalRef = null;
            } else {
              this.modal.close();
            }

            this.getUserList();
            const editAccess = this.isAccess('', 'edit');
            editAccess && this.getUserInfoMethod.next();
          })
        )
        .subscribe(res => {
          if (res) {
            if (!res.messageType) {
              this.toaster.success(res.message);
            } else {
              this.toaster.error(res.message);
            }
          }
        });
    }
  }
}
